/**
 * Returns the first day of the week for the current user's locale.
 *
 * This function uses the `Intl.Locale` API to determine the first day of the week
 * based on the user's browser language settings. It checks for the presence of
 * `getWeekInfo()` or `weekInfo` properties on the `Intl.Locale` object to retrieve
 * the information. If neither is available, it defaults to Sunday to match the app's
 * `en-US` locale.
 *
 * The `Intl` week-info API reports `firstDay` using ISO-8601 numbering
 * (1 = Monday … 7 = Sunday). This is normalized with `% 7` to JavaScript's
 * `Date.getDay()` numbering (0 = Sunday … 6 = Saturday) so callers can compare the
 * result directly against `getDay()`.
 *
 * @returns {number} The numeric representation of the first day of the week (0 = Sunday, 1 = Monday, etc.).
 */
export const firstDayOfWeek = (): number => {
  const locale = new Intl.Locale(navigator.language);

  if (typeof locale.getWeekInfo === 'function') {
    return locale.getWeekInfo().firstDay % 7;
  }

  if ('weekInfo' in locale) {
    return locale.weekInfo.firstDay % 7;
  }

  return 0;
};
